import { Api } from '@monorepo/shared/apiClient';
import {
  WorkflowCacheEvents,
  workflowCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/workflowCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type KeyParams = Api.FetchWorkflowsProducingIncompleteDataParams;

const KEY_START = 'org-workflows-producing-incomplete-data' as const;
export const createKey = ({ organizationId, workflowId }: KeyParams) =>
  [KEY_START, organizationId, workflowId] as const;

type Fetcher = Api.DataHookQueryFn<
  typeof createKey,
  typeof Api.fetchWorkflowsProducingIncompleteData
>;

const fetcher: Fetcher = (_, organizationId, workflowId) =>
  Api.fetchWorkflowsProducingIncompleteData({ organizationId, workflowId });

type UseWorkflowsProducingIncompleteDataParams =
  Partial<Api.FetchWorkflowsProducingIncompleteDataParams> & {
    config?: QueryConfig<
      Api.FetchWorkflowsProducingIncompleteDataResponse,
      Api.ErrorResponse
    >;
  };

export function useWorkflowsProducingIncompleteData({
  config: inputConfig,
  organizationId,
  workflowId,
}: UseWorkflowsProducingIncompleteDataParams) {
  const isEnabled = !!organizationId && !!workflowId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled ? createKey({ organizationId, workflowId }) : undefined;

  const { data, ...queryInfo } = useQuery(key, fetcher, config);
  return {
    ...queryInfo,
    workflowsProducingIncompleteData: data,
  };
}

async function invalidateCache(
  keyParams: Pick<KeyParams, 'organizationId'>,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([KEY_START, keyParams.organizationId]),
  );
}

workflowCacheRegister([WorkflowCacheEvents.UPDATE], {
  hookName: 'useWorkflowsProducingIncompleteData',
  callback: ({ organizationId }) => invalidateCache({ organizationId }),
});
